import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntities as getLocations } from 'app/entities/location/location.reducer';
import { getEntities as getFleets } from 'app/entities/fleet/fleet.reducer';
import { StationType } from 'app/shared/model/enumerations/station-type.model';
import { createEntity, getEntity, updateEntity } from './station.reducer';
import { coordToString, stringToCoord } from 'app/shared/model/geo-point.model';

export const StationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const locations = useAppSelector(state => state.location.entities);
  const fleets = useAppSelector(state => state.fleet.entities);
  const stationEntity = useAppSelector(state => state.station.entity);
  const loading = useAppSelector(state => state.station.loading);
  const updating = useAppSelector(state => state.station.updating);
  const updateSuccess = useAppSelector(state => state.station.updateSuccess);
  const stationTypeValues = Object.keys(StationType);

  const handleClose = () => {
    navigate('/station');
  };

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id));
    }

    dispatch(getLocations({}));
    dispatch(getFleets({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  // eslint-disable-next-line complexity
  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.position = stringToCoord(values.positionAsString);

    const entity = {
      ...stationEntity,
      ...values,
      location: locations.find(it => it.id.toString() === values.location?.toString()),
      fleet: fleets.find(it => it.id.toString() === values.fleet?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          type: 'TLC_CABINET',
          ...stationEntity,
          positionAsString: coordToString(stationEntity?.position),
          location: stationEntity?.location?.id,
          fleet: stationEntity?.fleet?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="cabmanApp.station.home.createOrEditLabel" data-cy="StationCreateUpdateHeading">
            <Translate contentKey="cabmanApp.station.home.createOrEditLabel">Create or edit a Station</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="station-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField label={translate('cabmanApp.station.type')} id="station-type" name="type" data-cy="type" type="select">
                {stationTypeValues.map(stationType => (
                  <option value={stationType} key={stationType}>
                    {translate('cabmanApp.StationType.' + stationType)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('cabmanApp.station.serialNumber')}
                id="station-serialNumber"
                name="serialNumber"
                data-cy="serialNumber"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 64, message: translate('entity.validation.maxlength', { max: 64 }) },
                }}
              />
              <ValidatedField
                label={translate('cabmanApp.station.model')}
                id="station-model"
                name="model"
                data-cy="model"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 32, message: translate('entity.validation.maxlength', { max: 32 }) },
                }}
              />
              <ValidatedField
                label={translate('cabmanApp.station.name')}
                id="station-name"
                name="name"
                data-cy="name"
                type="text"
                validate={{
                  maxLength: { value: 64, message: translate('entity.validation.maxlength', { max: 64 }) },
                }}
              />
              <ValidatedField
                label={translate('cabmanApp.station.position')}
                id="station-position"
                name="positionAsString"
                data-cy="position"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  pattern: {
                    value: /^-?\d+(\.\d+)?,(\s)*-?\d+(\.\d+)?$/,
                    message: translate('entity.validation.pattern', {
                      pattern: '/^-?d+(.d+)?,(\\s)*-?d+(.d+)?$/',
                    }),
                  },
                }}
              />
              <ValidatedField
                id="station-location"
                name="location"
                data-cy="location"
                label={translate('cabmanApp.station.location')}
                type="select"
              >
                <option value="" key="0" />
                {locations
                  ? locations.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField id="station-fleet" name="fleet" data-cy="fleet" label={translate('cabmanApp.station.fleet')} type="select">
                <option value="" key="0" />
                {fleets
                  ? fleets.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/station" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default StationUpdate;
